import { X, Check, AlertTriangle, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const ROWS = [
  {
    neg: {
      title: "Alucinaciones peligrosas",
      body:
        "ChatGPT inventa libros, autores y ligas web. Si tu profesor detecta una cita falsa, reprobarás por completo.",
    },
    pos: {
      title: "Citas científicas reales",
      body:
        "Nuestra IA está conectada a bases de datos académicas verificadas. Cada fuente tiene un código DOI real y existente.",
    },
  },
  {
    neg: {
      title: "Pesadilla de formato",
      body:
        "Las IA tradicionales no saben si tu universidad exige APA 7, Vancouver, márgenes específicos o capitulado estricto.",
    },
    pos: {
      title: "Fábrica de plantillas",
      body:
        "Subes el reglamento de tu universidad y el editor se adapta automáticamente. Exportas tu documento listo para entregar.",
    },
  },
  {
    neg: {
      title: "Pánico al detector de plagio",
      body:
        "El texto generado de forma libre suena robótico y es fácilmente rebotado por herramientas como Turnitin.",
    },
    pos: {
      title: "Métrica antiplagio humana",
      body:
        "Analizamos tu redacción y te sugerimos cambios en voz activa académica para que pase cualquier filtro escolar.",
    },
  },
  {
    neg: {
      title: "Te deja solo en el paso final",
      body:
        "Ninguna IA comercial te prepara para el miedo y la presión de pararte frente a los sinodales de tu carrera.",
    },
    pos: {
      title: "Simulador de jurado",
      body:
        "Un chat interactivo te bombardea con las preguntas más difíciles de tu propia tesis para que entrenes tu defensa oral.",
    },
  },
];

export default function Comparative() {
  return (
    <section
      id="caracteristicas"
      data-testid="comparative-section"
      className="relative py-20 sm:py-24 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Heading */}
        <div className="max-w-3xl">
          <p className="text-[11px] font-medium tracking-[0.22em] uppercase text-copper">
            Por qué unaTESIS
          </p>
          <h2
            data-testid="comparative-title"
            className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-ink text-balance"
          >
            ¿Por qué pagar unaTESIS en lugar de usar{" "}
            <span className="italic text-ink-soft">IA gratis</span>?
          </h2>
          <p className="mt-5 text-[15px] text-ink-soft max-w-2xl leading-relaxed">
            La diferencia entre presentar tu tesis con confianza o ser
            descubierto en la revisión. Comparamos lo que pasa cuando confías tu
            futuro académico a una IA genérica frente a un copiloto entrenado
            para tu universidad.
          </p>
        </div>

        {/* Two columns */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {/* Negative column */}
          <div
            data-testid="comparative-negative-column"
            className="relative p-7 sm:p-9 rounded-2xl border border-rule bg-white/50"
          >
            <div className="flex items-center gap-2.5 pb-5 border-b border-rule">
              <span className="grid place-items-center h-9 w-9 rounded-full bg-[#FBE9E5] text-[#B6533F]">
                <AlertTriangle size={16} />
              </span>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#B6533F] font-medium">
                  Camino arriesgado
                </p>
                <p className="font-serif text-xl text-ink leading-tight">
                  Hacerla por tu cuenta con{" "}
                  <span className="italic">IA genérica</span>
                </p>
              </div>
            </div>

            <ul className="mt-6 flex flex-col gap-6">
              {ROWS.map((r, i) => (
                <li
                  key={`neg-${i}`}
                  data-testid={`comparative-neg-item-${i}`}
                  className="flex gap-3 items-start"
                >
                  <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full bg-[#FBE9E5] text-[#B6533F] shrink-0">
                    <X size={13} strokeWidth={2.5} />
                  </span>
                  <div>
                    <p className="font-serif text-[17px] text-ink leading-snug">
                      {r.neg.title}
                    </p>
                    <p className="mt-1 text-[13.5px] text-ink-soft leading-relaxed">
                      {r.neg.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Positive column */}
          <div
            data-testid="comparative-positive-column"
            className="relative p-7 sm:p-9 rounded-2xl border border-forest/15 bg-white shadow-[0_30px_60px_-25px_rgba(39,59,44,0.18)]"
          >
            {/* Copper top accent */}
            <span
              aria-hidden="true"
              className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-copper to-transparent"
            />

            <div className="flex items-center gap-2.5 pb-5 border-b border-rule">
              <span className="grid place-items-center h-9 w-9 rounded-full bg-forest text-paper">
                <ShieldCheck size={16} />
              </span>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-copper font-medium">
                  Camino seguro
                </p>
                <p className="font-serif text-xl text-ink leading-tight">
                  Hacerla de forma segura con{" "}
                  <span className="italic text-forest">unaTESIS</span>
                </p>
              </div>
            </div>

            <ul className="mt-6 flex flex-col gap-6">
              {ROWS.map((r, i) => (
                <li
                  key={`pos-${i}`}
                  data-testid={`comparative-pos-item-${i}`}
                  className="flex gap-3 items-start"
                >
                  <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full bg-forest text-paper shrink-0">
                    <Check size={13} strokeWidth={3} />
                  </span>
                  <div>
                    <p className="font-serif text-[17px] text-ink leading-snug">
                      {r.pos.title}
                    </p>
                    <p className="mt-1 text-[13.5px] text-ink-soft leading-relaxed">
                      {r.pos.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            {/* Inline CTA */}
            <div className="mt-8 pt-6 border-t border-rule flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <p className="font-serif italic text-[15px] text-forest">
                “La tesis es tuya. Nosotros solo te aseguramos que llegue al
                día de tu defensa.”
              </p>
              <Link
                to="/signup"
                data-testid="comparative-cta"
                className="self-start sm:self-auto inline-flex items-center px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all whitespace-nowrap hover:-translate-y-[1px] shadow-sm hover:shadow-md"
              >
                Probar gratis 60 s
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
